import React, { lazy, Suspense, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AttachFile,
  Clear,
  DeleteOutline,
  ExpandMore,
  Search,
  Visibility,
} from "@mui/icons-material";
const ConcessionExcelPreview = lazy(() => import("./ConcessionExcelPreview"));

const formatted = (value) => {
  if (value === "") return "";
  const [integer, decimal] = String(value).split(".");
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (decimal != null ? `,${decimal}` : "");
};
const inputSx = {
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .MuiInputBase-input": { color: "#0f172a" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#475569" },
};

export default function PeajeReportCards({ rows, reportados, disabled, onChange, onEnviar }) {
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [observations, setObservations] = useState({});
  const [preview, setPreview] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const names = [...new Set(rows.map((row) => row.peaje))];
  const normalize = (value) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("es");
  const visibleNames = names.filter((name) => normalize(name).includes(normalize(search.trim())));
  const change = (index, field, text) => {
    const value = text.replace(/\./g, "").replace(",", ".");
    if (!(field === "steps" ? /^\d*$/ : /^\d*(\.\d{0,2})?$/).test(value)) return;
    onChange((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };
  const attach = (name, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled || reportados[name]) return;
    const error = !/\.(xlsx|xls)$/i.test(file.name)
      ? "Selecciona un Excel .xlsx o .xls."
      : !file.size || file.size > 10 * 1024 * 1024
      ? "El Excel debe tener contenido y pesar máximo 10 MB."
      : "";
    setErrors((previous) => ({ ...previous, [name]: error }));
    if (!error) setFiles((previous) => ({ ...previous, [name]: file }));
  };
  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        size="medium"
        label="Buscar peaje"
        placeholder="Escribe el nombre del peaje"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{
          ...inputSx,
          "& .MuiInputBase-root": { minHeight: 52, fontSize: 15 },
          "& .MuiInputLabel-root": { color: "#475569", fontSize: 15 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Limpiar búsqueda de peajes"
                onClick={() => setSearch("")}
              >
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Typography role="status" sx={{ fontSize: 12, color: "#475569" }}>
        {visibleNames.length
          ? `Mostrando ${visibleNames.length} de ${names.length} peajes`
          : "No se encontraron peajes con ese nombre."}
      </Typography>
      {visibleNames.map((name) => {
        const saved = reportados[name];
        const savedFile = saved ? Object.values(saved)[0] : null;
        const fileName = saved ? savedFile?.fileName : files[name]?.name;
        const displayRows = saved
          ? Object.entries(saved).map(([llave, detail]) => ({
              row: {
                peaje: name,
                llave: llave || rows.find((row) => row.peaje === name)?.llave || "—",
              },
              index: -1,
              detail,
            }))
          : rows.map((row, index) => ({ row, index })).filter(({ row }) => row.peaje === name);
        return (
          <Box
            key={name}
            role="group"
            aria-label={`Reporte ${name}`}
            sx={{
              border: 1,
              borderColor: saved ? "success.light" : "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: saved ? "#ecfdf5" : "#edf5f7",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#16485a" }}>
                {name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={saved ? "Reportado" : "Pendiente"}
                  color={saved ? "success" : "default"}
                  sx={{
                    bgcolor: saved ? "#dcfce7" : "#fef3c7",
                    color: saved ? "#166534 !important" : "#92400e !important",
                    fontWeight: 700,
                  }}
                />
                {saved && /^[1-9]\d*$/.test(String(savedFile?.responseId)) && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    aria-label={`Ver Excel de ${name}`}
                    onClick={() =>
                      setPreview({
                        responseId: savedFile.responseId,
                        fileName,
                        peaje: savedFile.filePeaje,
                      })
                    }
                    sx={{
                      "&&": {
                        color: "#16485a",
                        borderColor: "#16485a",
                        bgcolor: "#fff",
                        textTransform: "none",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    Ver Excel
                  </Button>
                )}
                {saved && (
                  <IconButton
                    size="small"
                    aria-label={`Ver detalle de ${name}`}
                    aria-expanded={Boolean(expanded[name])}
                    onClick={() =>
                      setExpanded((previous) => ({ ...previous, [name]: !previous[name] }))
                    }
                    sx={{ color: "#166534", transform: expanded[name] ? "rotate(180deg)" : "none" }}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            {(!saved || expanded[name]) && (
              <Stack spacing={2} sx={{ p: 2 }}>
                {displayRows.map(({ row, index, detail }) => {
                  return (
                    <Stack key={row.llave} spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.5 }}>
                          Fecha de conciliación
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.primary",
                            fontWeight: 600,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {row.llave}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        {saved ? (
                          <>
                            <Box sx={{ p: 1.5, bgcolor: "#f1f5f9", borderRadius: 1.5 }}>
                              <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.5 }}>
                                Cantidad de pasos
                              </Typography>
                              <Typography
                                aria-label={`Pasos reportados de ${name}`}
                                sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}
                              >
                                {Number(detail.steps).toLocaleString("es-CO")}
                              </Typography>
                            </Box>
                            <Box sx={{ p: 1.5, bgcolor: "#f1f5f9", borderRadius: 1.5 }}>
                              <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.5 }}>
                                Valor reportado · COP
                              </Typography>
                              <Typography
                                aria-label={`Valor reportado de ${name}`}
                                sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}
                              >
                                {Number(detail.value).toLocaleString("es-CO", {
                                  style: "currency",
                                  currency: "COP",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <>
                            <TextField
                              fullWidth
                              required
                              label="Cantidad de pasos"
                              disabled={disabled || Boolean(saved)}
                              value={formatted(row.steps)}
                              onChange={(event) => change(index, "steps", event.target.value)}
                              inputProps={{ inputMode: "numeric" }}
                              sx={inputSx}
                            />
                            <TextField
                              fullWidth
                              required
                              label="Valor total (COP)"
                              disabled={disabled || Boolean(saved)}
                              value={formatted(row.value)}
                              onChange={(event) => change(index, "value", event.target.value)}
                              inputProps={{ inputMode: "decimal" }}
                              sx={inputSx}
                            />
                          </>
                        )}
                      </Box>
                    </Stack>
                  );
                })}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Observación"
                  disabled={disabled || Boolean(saved)}
                  value={
                    saved ? Object.values(saved)[0]?.observation || "" : observations[name] || ""
                  }
                  onChange={(event) =>
                    setObservations((previous) => ({ ...previous, [name]: event.target.value }))
                  }
                  helperText="Opcional · Observación de este peaje."
                  sx={inputSx}
                />
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px dashed #b6cbd2",
                    bgcolor: "#f8fafc",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "text.primary", fontWeight: 600, mb: 1 }}>
                    Archivo Excel ·{" "}
                    {saved ? (fileName ? "Enviado" : "Sin archivo asociado") : "Obligatorio"}
                  </Typography>
                  {!saved && (
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFile />}
                      disabled={disabled}
                      sx={{
                        "&&": {
                          color: "#16485a",
                          borderColor: "#16485a",
                          bgcolor: "#fff",
                          textTransform: "none",
                          fontWeight: 700,
                        },
                        "&&:hover": { bgcolor: "#edf5f7", borderColor: "#0b4f6c" },
                        "&&.Mui-disabled": {
                          color: "#64748b",
                          borderColor: "#cbd5e1",
                          bgcolor: "#f1f5f9",
                        },
                        "& .MuiSvgIcon-root": { color: "inherit" },
                      }}
                    >
                      {files[name] ? "Cambiar Excel" : "Adjuntar Excel"}
                      <input
                        hidden
                        type="file"
                        accept=".xlsx,.xls"
                        aria-label={`Seleccionar Excel de ${name}`}
                        onChange={(event) => attach(name, event)}
                        disabled={disabled}
                      />
                    </Button>
                  )}
                  {fileName && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "text.primary",
                          overflowWrap: "anywhere",
                          flex: 1,
                        }}
                      >
                        {fileName}
                      </Typography>
                      {!saved && (
                        <IconButton
                          size="small"
                          aria-label={`Quitar Excel de ${name}`}
                          disabled={disabled}
                          sx={{ color: "#b91c1c" }}
                          onClick={() => {
                            setFiles((previous) => ({ ...previous, [name]: null }));
                            setErrors((previous) => ({ ...previous, [name]: "" }));
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                  {!saved && (
                    <Typography sx={{ fontSize: 11, color: "#475569", mt: 1 }}>
                      .xlsx o .xls · Máximo 10 MB por peaje
                    </Typography>
                  )}
                  {errors[name] && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {errors[name]}
                    </Alert>
                  )}
                </Box>
                {!saved && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      disabled={disabled || Boolean(errors[name])}
                      onClick={() =>
                        onEnviar({
                          peaje: name,
                          file: files[name],
                          observation: observations[name] || "",
                        })
                      }
                      aria-label={`Enviar ${name}`}
                      sx={{
                        bgcolor: "#16485a",
                        color: "#fff !important",
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#0b3544", boxShadow: "none" },
                        "&&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#64748b !important" },
                      }}
                    >
                      Enviar este peaje
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
      {preview && (
        <Suspense
          fallback={<CircularProgress size={24} aria-label="Cargando vista previa del Excel" />}
        >
          <ConcessionExcelPreview
            responseId={preview.responseId}
            fileName={preview.fileName}
            peaje={preview.peaje}
            onClose={() => setPreview(null)}
          />
        </Suspense>
      )}
    </Stack>
  );
}
PeajeReportCards.propTypes = {
  rows: PropTypes.array.isRequired,
  reportados: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onEnviar: PropTypes.func.isRequired,
};
